import { request } from '@/api/apiUtils';
import { createEntityQuery } from '@/data/createEntityQuery';
import type { EntityDefinition, FetcherEntry } from '@/data/types';
import type { ICompany, ResponseType } from '@/types';

/** Root: query-key prefix for this entity's slice of the cache (ADR 0010) */
export const COMPANIES_ROOT = ['companies'] as const;

interface CompanyDetailParams {
    companyId: number;
}

interface CompaniesListParams {
    perPage?: number;
}

const fetchers = {
    detail: {
        key: ({ companyId }: CompanyDetailParams) => [...COMPANIES_ROOT, 'detail', companyId] as const,
        fetch: async ({ companyId }: CompanyDetailParams): Promise<ICompany> => {
            const response: ResponseType<ICompany> = await request(`companies/${companyId}`);
            return response.data;
        },
    },
    list: {
        key: ({ perPage }: CompaniesListParams) => [...COMPANIES_ROOT, 'list', { perPage }] as const,
        fetch: async ({ perPage = 100 }: CompaniesListParams): Promise<ICompany[]> => {
            const response: ResponseType<ICompany[]> = await request(`companies?per_page=${perPage}`);
            // Personal space ("Персональное пространство") is always pinned to the
            // top of the list; backend order is preserved for everything else
            // (Array.prototype.sort is stable). Canonical here so every consumer
            // of this fetcher inherits the ordering.
            return [...response.data].sort((a, b) => Number(b.is_personal) - Number(a.is_personal));
        },
    },
    // biome-ignore lint/suspicious/noExplicitAny: required for generic type inference
} satisfies Record<string, FetcherEntry<any, any>>;

export const companiesEntity: EntityDefinition<typeof fetchers> = {
    name: 'companies',
    roots: [COMPANIES_ROOT],
    fetchers,
};

export const useCompanyDetail = createEntityQuery(companiesEntity, 'detail');
export const useCompaniesList = createEntityQuery(companiesEntity, 'list');

export type { CompaniesListParams, CompanyDetailParams };
